import React, { useState } from 'react';
import { ChevronRightIcon, ImageOffIcon, ListChecksIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { QualityReport } from '../types/quality';
import { getStats } from '../utils/quality';
import { ProgressRing } from '../components/quality/ProgressRing';
import { NewQualityReportModal } from '../components/quality/NewQualityReportModal';
import { ProCustomer } from '../types/pro';
interface QualityAssuranceProps {
  reports: QualityReport[];
  customers: ProCustomer[];
  onOpenReport: (reportId: string) => void;
  onCreateReport: (report: Omit<QualityReport, 'id'>) => void;
  onDeleteReport: (report: QualityReport) => void;
  nextReportId: string;
  responsible: {
    name: string;
    email: string;
    phone: string;
  };
  canEdit: boolean;
}
export function QualityAssurance({
  reports,
  customers,
  onOpenReport,
  onCreateReport,
  onDeleteReport,
  nextReportId,
  responsible,
  canEdit
}: QualityAssuranceProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = reports.filter(r => r.status === 'Åben');
  const totalMissing = reports.reduce((sum, r) => sum + getStats(r).missingPhoto, 0);
  return <div data-id="element-1638" data-mp="g4yshhj6v301">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4" data-id="element-1639" data-mp="27jwftyfjwz3">
        <div data-id="element-1640" data-mp="29o48mzg9g4n">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-1641" data-mp="76mrclkp4mtc">
            Kvalitetssikring
          </h1>
          <p className="mt-2 text-stone-500" data-id="element-1642" data-mp="3ommj0fn96m6e">
            {open.length} åbne rapporter
            {totalMissing > 0 && ` · ${totalMissing} kontrolpunkter mangler foto`}
          </p>
        </div>
        {canEdit && <button onClick={() => setIsModalOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1643" data-mp="3fo14cvkwjraw">
            <PlusIcon className="h-4 w-4" data-id="element-1644" data-mp="3andeid8etgi9" />
            Ny kvalitetssikring
          </button>}
      </div>

      {reports.length === 0 ? <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-16 text-center" data-id="element-1645" data-mp="1g7y78f9idkx4">
          <ListChecksIcon className="mx-auto h-8 w-8 text-stone-300" data-id="element-1646" data-mp="1beve288n8dhg" />
          <h2 className="mt-3 text-base font-semibold text-stone-900" data-id="element-1647" data-mp="17e1qxh30z2pm">
            Ingen kvalitetssikringer endnu
          </h2>
          <p className="mt-1 text-sm text-stone-500" data-id="element-1648" data-mp="12hbagsnb653o">
            Opret en rapport for at dokumentere arbejdet med foto.
          </p>
          {canEdit && <button onClick={() => setIsModalOpen(true)} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1649" data-mp="1v3vx5fsu9t0w">
              <PlusIcon className="h-4 w-4" data-id="element-1650" data-mp="1l8c7hb4vbqkl" />
              Ny kvalitetssikring
            </button>}
        </div> : <ul className="space-y-3" data-id="element-1651" data-mp="9qjhe41jhwkk">
          {reports.map(report => {
        const stats = getStats(report);
        return <li key={report.id} className="flex items-center gap-2 rounded-2xl border border-stone-200 bg-white pr-3 transition-colors hover:border-brand-300" data-id="element-1652" data-mp="3vu39qbd699qw">
                <button onClick={() => onOpenReport(report.id)} className="flex min-w-0 flex-1 items-center gap-4 rounded-2xl p-5 text-left" data-id="element-1653" data-mp="9k0oluu6o67s">
                  <ProgressRing percent={stats.percent} size={52} data-id="element-1654" data-mp="iddqvne50k6j" />

                  <span className="min-w-0 flex-1" data-id="element-1655" data-mp="nbp0py4fmuor">
                    <span className="flex flex-wrap items-center gap-2" data-id="element-1656" data-mp="nejmpu33gnka">
                      <span className="font-mono text-xs font-semibold text-brand-700" data-id="element-1657" data-mp="scctao8b7jl3">
                        {report.reportId}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600" data-id="element-1658" data-mp="2u3pwn8umpjf1">
                        <span className={`h-1.5 w-1.5 rounded-full ${report.status === 'Åben' ? 'bg-emerald-500' : 'bg-stone-400'}`} data-id="element-1659" data-mp="2z2j52mv26k8c" />
                        {report.status}
                      </span>
                    </span>
                    <span className="mt-1 block truncate text-base font-semibold text-stone-900" data-id="element-1660" data-mp="xgdhue7ykchl">
                      {report.title}
                    </span>
                    <span className="mt-0.5 block truncate text-sm text-stone-500" data-id="element-1661" data-mp="5p7gjugi48qq">
                      {report.customer.name} · {stats.completedPoints}/
                      {stats.totalPoints} kontrolpunkter ·{' '}
                      {report.photos.length} fotos
                    </span>
                  </span>

                  {stats.missingPhoto > 0 && <span className="hidden flex-shrink-0 items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 sm:inline-flex" data-id="element-1662" data-mp="3i62x2sckq0at">
                      <ImageOffIcon className="h-3.5 w-3.5" data-id="element-1663" data-mp="1xmh86ue2osfd" />
                      {stats.missingPhoto} mangler foto
                    </span>}

                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-stone-300" data-id="element-1664" data-mp="1oo5cwf29h6gl" />
                </button>

                {canEdit && <button onClick={() => onDeleteReport(report)} aria-label={`Slet ${report.reportId}`} className="flex-shrink-0 rounded-lg p-2 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1665" data-mp="1tk2pcaaelso3">
                    <Trash2Icon className="h-4 w-4" data-id="element-1666" data-mp="27foeqikjueh1" />
                  </button>}
              </li>;
      })}
        </ul>}

      <NewQualityReportModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onCreate={onCreateReport} customers={customers} nextReportId={nextReportId} responsible={responsible} data-id="element-1667" data-mp="vxdj1o8o4twf" />
    </div>;
}